import { HttpErrorResponse, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, throwError } from 'rxjs';
import { ENVIRONMENT } from '../environment/environment.token';
import { AuthService } from './auth.service';

/**
 * Interceptor global de autenticação:
 * - não envia Authorization para endpoints de auth e S3
 * - trata 401/403 com limpeza de sessão e redirecionamento para login
 */
export const authBearerInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const environment = inject(ENVIRONMENT);
  const token = authService.getAuthorizationToken();
  const isAuthRequest = isAuthEndpoint(req.url, environment.apiUrl);
  const isS3Request = req.url.includes('s3.amazonaws.com');

  const requestWithAuth = shouldAttachAuthorization(isAuthRequest, isS3Request, token)
    ? req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`,
        },
      })
    : req;

  return next(requestWithAuth).pipe(
    catchError((error: HttpErrorResponse) => {
      if (!isAuthRequest) {
        handleAuthorizationError(error, router, environment.requireAuth);
      }

      return throwError(() => error);
    }),
  );
};

function isAuthEndpoint(url: string, apiUrl: string): boolean {
  const normalizedApiUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
  const authBase = `${normalizedApiUrl}/auth`;
  const explicitAuthEndpoints = new Set([authBase, `${authBase}/azureAD`, `${authBase}/basic`]);

  return explicitAuthEndpoints.has(url) || url.includes('/auth');
}

function shouldAttachAuthorization(isAuthRequest: boolean, isS3Request: boolean, token: string | null): token is string {
  return !isAuthRequest && !isS3Request && Boolean(token);
}

function handleAuthorizationError(error: HttpErrorResponse, router: Router, requireAuth: boolean): void {
  if (error.status !== 401 && error.status !== 403) {
    return;
  }

  if (!requireAuth) {
    return;
  }

  sessionStorage.clear();
  localStorage.clear();
  void router.navigate(['/login']);
}

